import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  model,
  viewChild,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { ICoordenadas } from 'modelos/src';
import { MapboxService, TipoMapaBase } from '../mapbox.service';
import { latLngToLngLat } from '../mapbox-coords';

/**
 * Selector de un punto en el mapa: marcador arrastrable + click para reubicar.
 * Two-way binding `[(coordenadas)]` con el modelo de la app (`ICoordenadas`).
 * Reemplaza el `<google-map><map-marker mapDragend>` de Google Maps.
 */
@Component({
  selector: 'app-mapa-picker',
  standalone: true,
  template: `
    <div class="picker-wrapper" [style.height]="height()">
      <div #cont class="picker-cont"></div>
      <button
        type="button"
        class="picker-toggle"
        (click)="toggleEstilo()"
        title="Cambiar tipo de mapa"
      >
        {{ estilo === 'calle' ? 'Satélite' : 'Calle' }}
      </button>
    </div>
  `,
  styles: [
    `
      .picker-wrapper {
        position: relative;
        width: 100%;
      }
      .picker-cont {
        width: 100%;
        height: 100%;
        border-radius: 10px;
        overflow: hidden;
      }
      .picker-toggle {
        position: absolute;
        bottom: 10px;
        right: 10px;
        z-index: 1;
        border: none;
        border-radius: 8px;
        padding: 6px 12px;
        background: rgba(255, 255, 255, 0.95);
        color: #0c4767;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(15, 37, 53, 0.25);
      }
    `,
  ],
})
export class MapaPickerComponent implements AfterViewInit, OnDestroy {
  private mapboxService = inject(MapboxService);

  private readonly cont =
    viewChild.required<ElementRef<HTMLDivElement>>('cont');

  /** Punto seleccionado (two-way). */
  readonly coordenadas = model<ICoordenadas | undefined>(undefined);
  readonly zoom = input<number>(13);
  readonly height = input<string>('320px');

  public estilo: TipoMapaBase = 'calle';

  private map?: mapboxgl.Map;
  private marker?: mapboxgl.Marker;
  private cargado = false;
  private resizeObserver?: ResizeObserver;

  constructor() {
    // Cambios externos (ej. app-marker-location) reposicionan el marcador.
    effect(() => {
      const c = this.coordenadas();
      if (this.cargado && c) {
        this.colocar(c, false);
      }
    });
  }

  ngAfterViewInit(): void {
    this.map = this.mapboxService.crearMapa(this.cont().nativeElement, {
      center: this.coordenadas(),
      zoom: this.zoom(),
      estilo: this.estilo,
    });
    this.map.on('load', () => {
      this.cargado = true;
      const c = this.coordenadas();
      if (c) {
        this.colocar(c, true);
      }
    });
    // Click en el mapa reubica el marcador.
    this.map.on('click', (e) => {
      this.set({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.cont().nativeElement);
  }

  toggleEstilo(): void {
    this.estilo = this.estilo === 'calle' ? 'satelite' : 'calle';
    this.map?.setStyle(this.mapboxService.estiloUrl(this.estilo));
  }

  private colocar(c: ICoordenadas, centrar: boolean): void {
    if (!this.map) {
      return;
    }
    const lngLat = latLngToLngLat(c);
    if (this.marker) {
      this.marker.setLngLat(lngLat);
    } else {
      this.marker = new mapboxgl.Marker({ draggable: true, color: '#0c4767' })
        .setLngLat(lngLat)
        .addTo(this.map);
      this.marker.on('dragend', () => {
        const p = this.marker!.getLngLat();
        this.set({ lat: p.lat, lng: p.lng });
      });
    }
    if (centrar) {
      this.map.setCenter(lngLat);
    }
  }

  /** Actualiza el modelo solo si cambió (evita loops con el effect). */
  private set(c: ICoordenadas): void {
    const cur = this.coordenadas();
    if (!cur || cur.lat !== c.lat || cur.lng !== c.lng) {
      this.coordenadas.set(c);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.marker?.remove();
    this.map?.remove();
  }
}
